<template>
    <div class="dbhome-page">
        <!-- 未登录提示 -->
        <div v-if="!isLogin" class="dbhome-notice no-select">
            <el-icon class="dbhome-notice__icon">
                <Coin />
            </el-icon>
            <span>登录后可执行共享的数据库 SQL</span>
        </div>

        <!-- 工具条：团队筛选 + 搜索 -->
        <div class="dbhome-toolbar no-select">
            <div class="dbhome-toolbar__filter" v-if="isLogin">
                <el-select v-model="selectedTeamId" class="dbhome-team" placeholder="全部团队" clearable>
                    <el-option v-for="team in visibleTeamList" :key="team.id"
                        :label="`${team.name} 【${teamMatchCounts[team.id] || 0}】`" :value="team.id" />
                </el-select>
            </div>
            <div class="dbhome-toolbar__right">
                <el-input v-model="searchKeyword" class="dbhome-search" placeholder="搜索连接名称或地址" clearable>
                    <template #prefix>
                        <el-icon>
                            <Search />
                        </el-icon>
                    </template>
                </el-input>
            </div>
        </div>

        <!-- 连接卡片：按分组展示 -->
        <div class="dbhome-group" v-for="group in connGroupList" :key="group.key">
            <div class="dbhome-group__head" v-if="showGroupHeader">
                <span class="dbhome-group__name">{{ group.name }}</span>
                <span class="dbhome-group__count">{{ group.list.length }} 个连接</span>
            </div>
            <div class="dbhome-grid no-select">
                <article class="dbhome-card" v-for="conn in group.list" :key="conn.id">
                    <header class="dbhome-card__head">
                        <div class="dbhome-card__title">
                            <div class="dbhome-card__name" :title="conn.name">{{ conn.name }}</div>
                            <div class="dbhome-card__addr">{{ conn.host }}:{{ conn.port }}/{{ conn.defaultDb }}</div>
                        </div>
                        <div class="dbhome-card__tags">
                            <el-tag size="small" :type="conn.canWrite ? 'warning' : 'info'" effect="plain">
                                {{ conn.canWrite ? '可写' : '只读' }}</el-tag>
                            <el-tag v-if="conn.prodLock" size="small" type="danger" effect="plain">生产防护</el-tag>
                            <el-tag size="small" effect="plain">{{ conn.dbType }}</el-tag>
                        </div>
                    </header>

                    <div class="dbhome-card__body">
                        <p class="dbhome-card__remark">{{ conn.remark || '暂无描述' }}</p>
                        <div class="dbhome-card__teams">
                            <span class="team-label">只读团队：</span>
                            <el-tag v-for="t in (conn.teamList || [])" :key="t.id" size="small" type="info"
                                effect="plain">{{ t.name }}</el-tag>
                            <span v-if="!(conn.teamList || []).length" class="team-empty">无</span>
                            <span class="team-label" style="margin-left:10px;">读写团队：</span>
                            <el-tag v-for="t in (conn.rwTeamList || [])" :key="t.id" size="small" type="warning"
                                effect="plain">{{ t.name }}</el-tag>
                            <span v-if="!(conn.rwTeamList || []).length" class="team-empty">无</span>
                        </div>
                    </div>

                    <footer class="dbhome-card__foot">
                        <span class="dbhome-card__cnt">{{ conn.sqlBlockCount || 0 }} 个 SQL 块</span>
                        <el-button class="dbhome-card__open" type="primary" plain size="small"
                            @click="openConsole(conn)">
                            <el-icon class="el-icon--left">
                                <Monitor />
                            </el-icon>
                            打开控制台
                        </el-button>
                    </footer>
                </article>
            </div>
        </div>

        <!-- 空状态 -->
        <div class="dbhome-empty" v-if="listLoaded && filteredList.length === 0">
            <el-icon :size="26">
                <Search />
            </el-icon>
            <p>{{ searchKeyword || selectedTeamId ? '没有匹配的连接' : (isLogin ? '暂无共享的数据库连接' : '登录后查看共享连接') }}</p>
        </div>

        <!-- SQL 执行控制台 -->
        <db-console-drawer v-model:visible="consoleDrawer.visible" :conn="consoleDrawer.conn"
            @changed="getShareList" />
    </div>
</template>

<script>
import request from '../../utils/request.js';
import DbConsoleDrawer from './components/DbConsoleDrawer.vue';

const UNGROUPED_KEY = '__ungrouped__';

export default {
    name: 'DataBaseMan',
    components: { DbConsoleDrawer },
    data() {
        return {
            isLogin: false,
            searchKeyword: '',
            selectedTeamId: '',
            teamStorageKey: 'hidoc_dbman_selected_team_id',
            teamList: [],
            connList: [],
            listLoaded: false,
            consoleDrawer: { visible: false, conn: {} }
        };
    },
    watch: {
        selectedTeamId(val) {
            localStorage.setItem(this.teamStorageKey, val || '');
        }
    },
    computed: {
        keywordFilteredList() {
            const list = this.connList;
            if (!this.searchKeyword) return list;
            const keywords = this.searchKeyword.trim().split(/\s+/).filter(k => k.length > 0);
            if (keywords.length === 0) return list;
            return list.filter(item => keywords.every(keyword => {
                const kw = keyword.toLowerCase();
                // SQL 块明细已不在 shareList 下发（改为打开控制台时按需分页拉取），这里只搜连接本身
                return (item.name || '').toLowerCase().includes(kw)
                    || (item.host || '').toLowerCase().includes(kw);
            }));
        },
        filteredList() {
            if (!this.selectedTeamId) return this.keywordFilteredList;
            return this.keywordFilteredList.filter(item =>
                (item.teamList || []).some(t => t.id === this.selectedTeamId)
                || (item.rwTeamList || []).some(t => t.id === this.selectedTeamId));
        },
        teamMatchCounts() {
            const counts = {};
            this.teamList.forEach(team => {
                counts[team.id] = 0;
            });
            this.keywordFilteredList.forEach(item => {
                const ids = [];
                (item.teamList || []).forEach(t => ids.push(t.id));
                (item.rwTeamList || []).forEach(t => ids.push(t.id));
                ids.forEach(id => {
                    if (counts[id] !== undefined) counts[id]++;
                });
            });
            return counts;
        },
        visibleTeamList() {
            return this.teamList.filter(team =>
                team.id === this.selectedTeamId || (this.teamMatchCounts[team.id] || 0) > 0
            );
        },
        connGroupList() {
            const groups = new Map();
            this.filteredList.forEach(conn => {
                const name = (conn.groupName || '').trim();
                const key = name || UNGROUPED_KEY;
                if (!groups.has(key)) {
                    groups.set(key, { key, name: name || '未分组', list: [] });
                }
                groups.get(key).list.push(conn);
            });
            const named = [];
            let ungrouped = null;
            groups.forEach(group => {
                if (group.key === UNGROUPED_KEY) ungrouped = group;
                else named.push(group);
            });
            named.sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'));
            return ungrouped ? named.concat(ungrouped) : named;
        },
        showGroupHeader() {
            return this.connGroupList.some(group => group.key !== UNGROUPED_KEY);
        }
    },
    mounted() {
        document.title = 'Hidoc-数据库管理';
        this.isLogin = !!(this.$store.state.user.token);
        if (this.isLogin) {
            this.getTeamFilterOptions();
        }
        this.getShareList();
    },
    methods: {
        getShareList() {
            return request({
                url: '/dbMan/shareList',
                method: 'post',
                data: { token: this.$store.state.user.token }
            }).then(res => {
                if (res.code == 0) {
                    this.connList = res.data || [];
                    // 控制台已打开时，同步刷新其连接数据
                    if (this.consoleDrawer.visible) {
                        const latest = this.connList.find(c => c.id === this.consoleDrawer.conn.id);
                        if (latest) this.consoleDrawer.conn = latest;
                    }
                }
                this.listLoaded = true;
            });
        },
        getTeamFilterOptions() {
            return request({
                url: '/team/getMyJoinTeams',
                method: 'post',
                data: { token: this.$store.state.user.token }
            }).then(res => {
                if (res.code == 0 && res.data) {
                    this.teamList = res.data.map(t => ({ id: t.id, name: t.name }))
                        .sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'));
                    const cached = localStorage.getItem(this.teamStorageKey);
                    if (cached && this.teamList.some(t => t.id === cached)) {
                        this.selectedTeamId = cached;
                    }
                }
            });
        },
        openConsole(conn) {
            if (!this.isLogin) return;
            this.consoleDrawer.conn = conn;
            this.consoleDrawer.visible = true;
        }
    }
};
</script>

<style scoped>
.dbhome-page {
    --el-color-primary: #2f6fed;
    --el-color-primary-light-3: #6d9af2;
    --el-color-primary-light-5: #97b7f6;
    --el-color-primary-light-7: #c1d4fa;
    --el-color-primary-light-8: #d5e2fb;
    --el-color-primary-light-9: #eaf1fd;
    --el-color-primary-dark-2: #2659be;

    --dbhome-text: #1f2d3d;
    --dbhome-text-sub: #5b6a80;
    --dbhome-text-weak: #8a94a6;
    --dbhome-line: #e6eaf0;

    color: var(--dbhome-text);
}

.dbhome-notice {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    padding: 10px 14px;
    border: 1px solid var(--el-color-primary-light-7);
    border-radius: 10px;
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary-dark-2);
    font-size: 13px;
}

.dbhome-notice__icon {
    flex: 0 0 auto;
    font-size: 15px;
}

.dbhome-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
}

.dbhome-team {
    width: 240px;
}

.dbhome-toolbar__right {
    display: flex;
    flex: 0 1 auto;
    align-items: center;
    gap: 10px;
    margin-left: auto;
}

.dbhome-search {
    width: 260px;
}

.dbhome-group__head {
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border: 1px solid var(--el-color-primary-light-7);
    border-left: 3px solid var(--el-color-primary);
    border-radius: 8px;
    background: linear-gradient(90deg, var(--el-color-primary-light-8) 0%, var(--el-color-primary-light-9) 100%);
}

.dbhome-group__name {
    font-size: 14px;
    font-weight: 700;
    color: var(--el-color-primary-dark-2);
}

.dbhome-group__count {
    padding: 2px 8px;
    border: 1px solid var(--el-color-primary-light-7);
    border-radius: 999px;
    background-color: #fff;
    color: var(--el-color-primary);
    font-size: 12px;
}

.dbhome-grid {
    column-width: 360px;
    column-gap: 16px;
    margin-top: 14px;
    padding-bottom: 8px;
}

.dbhome-card {
    margin: 0 0 16px;
    break-inside: avoid;
    border: 1px solid var(--dbhome-line);
    border-radius: 14px;
    background-color: #ffffff;
    box-shadow: 0 1px 2px rgba(28, 47, 84, 0.05);
    overflow: hidden;
    transition: box-shadow 0.25s, transform 0.25s, border-color 0.25s;
}

.dbhome-card:hover {
    border-color: #d8e1ef;
    box-shadow: 0 14px 30px -16px rgba(28, 47, 84, 0.3);
    transform: translateY(-2px);
}

.dbhome-card__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    row-gap: 8px;
    padding: 12px 14px;
    border-bottom: 1px solid #eef1f6;
    background: linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%);
}

.dbhome-card__title {
    flex: 1 1 160px;
    min-width: 0;
}

.dbhome-card__name {
    font-size: 14px;
    font-weight: 600;
    color: var(--dbhome-text);
}

.dbhome-card__addr {
    margin-top: 2px;
    font-size: 12px;
    color: #aab4c4;
}

.dbhome-card__tags {
    display: flex;
    flex: none;
    flex-wrap: wrap;
    gap: 6px;
    margin-left: auto;
}

.dbhome-card__body {
    padding: 10px 14px;
}

.dbhome-card__remark {
    margin: 0 0 8px;
    font-size: 13px;
    color: var(--dbhome-text-sub);
}

.dbhome-card__teams {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    font-size: 12px;
}

.team-label {
    color: #8a94a6;
}

.team-empty {
    color: #aab4c4;
}

.dbhome-card__foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 14px;
    border-top: 1px dashed #eef1f6;
}

.dbhome-card__cnt {
    font-size: 12px;
    color: #aab4c4;
}

.dbhome-card__open {
    flex: 0 0 auto;
}

.dbhome-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 56px 20px;
    border: 1px dashed #dcdfe6;
    border-radius: 12px;
    background-color: #ffffff;
    color: #909399;
    font-size: 13px;
}

.dbhome-empty p {
    margin: 0;
}

@media (max-width: 768px) {
    .dbhome-toolbar {
        flex-direction: column;
        align-items: stretch;
    }

    .dbhome-team,
    .dbhome-search {
        width: 100%;
    }

    .dbhome-toolbar__right {
        width: 100%;
        margin-left: 0;
    }
}
</style>
